import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAICalendar } from "contexts/AICalendarContext";
import _request from "actions/_request";
import i18n from "i18";
import {
  resolveCalendarPostAccounts,
  describeUnresolvedSlot,
} from "utils/calendarAccounts";
import { normaliseGeneratedContent } from "utils/calendarContent";

/**
 * Step 6: Finalize
 * Confirm and save posts to schedule
 * @param {Object} props - Component props
 * @param {Function} props.onComplete - Function called on completion
 * @returns {JSX.Element} Step 6 Finalization controls
 */
export default function Step6Finalize({ onComplete }) {
  const { state, prevStep } = useAICalendar();
  
  const NEW_COLLECTION_SENTINEL = "__new__";

  const [finalizing, setFinalizing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");

  // Load draft collections only when the user actually flips saveAsDraft on,
  // so normal (publish-directly) flow pays no extra request.
  useEffect(() => {
    if (!saveAsDraft) return;
    let cancelled = false;
    (async () => {
      setCollectionsLoading(true);
      const resp = await _request.draftCollections.getAll();
      if (cancelled) return;
      const items = resp?.data?.items || [];
      setCollections(items);
      setSelectedCollectionId((prev) => {
        if (prev) return prev;
        return items.length > 0 ? items[0].id : NEW_COLLECTION_SENTINEL;
      });
      setNewCollectionName((prev) =>
        prev || `AI Calendar ${new Date().toISOString().slice(0, 10)}`,
      );
      setCollectionsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [saveAsDraft]);

  const isCreatingNewCollection = selectedCollectionId === NEW_COLLECTION_SENTINEL;

  // Resolve the target draft_collection_id, creating a new collection on the
  // fly if the user chose "create new". Returns null on hard failure and sets
  // a local error; returns undefined when not saving as draft.
  const resolveDraftCollectionId = async () => {
    if (!saveAsDraft) return undefined;
    if (isCreatingNewCollection) {
      const title = newCollectionName.trim();
      if (!title) {
        setLocalError(i18n.t("aiCalendarWizard.step6.errorCollectionNameRequired"));
        return null;
      }
      const created = await _request.draftCollections.add({ title });
      if (!created?.isOk) {
        setLocalError(created?.error || i18n.t("aiCalendarWizard.errors.generic"));
        return null;
      }
      return created?.data?.id;
    }
    if (!selectedCollectionId) {
      setLocalError(i18n.t("aiCalendarWizard.step6.errorCollectionRequired"));
      return null;
    }
    return selectedCollectionId;
  };

  const handleFinalize = async () => {
    setLocalError(null);
    setFinalizing(true);

    try {
      const draftCollectionId = await resolveDraftCollectionId();
      if (saveAsDraft && (draftCollectionId === null || draftCollectionId === undefined)) {
        setFinalizing(false);
        return;
      }

      // Every post needs a real account id — finalize validates them against
      // `accounts.id`, so a slot we cannot resolve has to stop us here rather
      // than be sent as null and fail the whole request (OP#2494).
      const { resolved, unresolved } = resolveCalendarPostAccounts(
        state.generatedPosts,
        state.selectedAccountDetails,
      );

      if (unresolved.length > 0) {
        setLocalError(
          i18n.t("aiCalendarWizard.errors.accountUnresolved", {
            slots: unresolved.map(describeUnresolvedSlot).join(", "),
          }),
        );
        setFinalizing(false);
        return;
      }

      // Transform generated posts to finalize format. Content goes through the
      // same normaliser as the review step, so a JSON envelope can never be
      // stored as the caption even if the user skipped straight past Review.
      const posts = resolved.map((post) => {
        const content = normaliseGeneratedContent(post);
        return {
          scheduled_date: post.scheduled_date,
          scheduled_time: post.scheduled_time,
          account_ids: [post.account_id],
          platform: post.platform,
          type: "text",
          type_data: {
            text: content.text,
            title: content.title,
          },
          tags: content.hashtags,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      });

      const params = {
        posts,
        calendar_name: `AI Calendar ${new Date().toLocaleDateString()}`,
        draft_collection_id: draftCollectionId,
      };

      const response = await _request.aiCalendar.finalize(params);

      if (response?.isOk && response?.data) {
        setSummary(response.data.calendar_summary);
        setCompleted(true);
        
        // Wait a moment before calling onComplete
        setTimeout(() => {
          onComplete();
        }, 2000);
      } else {
        setLocalError(response?.error || i18n.t("aiCalendarWizard.errors.finalizeFailed"));
      }
    } catch (error) {
      console.error("Finalization failed:", error);
      setLocalError(error.message || i18n.t("aiCalendarWizard.errors.generic"));
    } finally {
      setFinalizing(false);
    }
  };

  if (completed) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: "success.main" }} />
        <Typography variant="h5">
          {i18n.t("aiCalendarWizard.step6.success")}
        </Typography>
        
        {summary && (
          <Card variant="outlined" sx={{ maxWidth: 600 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                {i18n.t("aiCalendarWizard.step6.summary")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("aiCalendarWizard.step6.totalPosts")}
                  </Typography>
                  <Typography variant="h6">{summary.total_posts}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("aiCalendarWizard.step6.dateRange")}
                  </Typography>
                  <Typography variant="body1">{summary.date_range}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {i18n.t("aiCalendarWizard.step6.platforms")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {Object.entries(summary.platforms || {}).map(([platform, count]) => (
                      <Typography key={platform} variant="body2">
                        {platform}: {count}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        
        <Typography variant="body2" color="text.secondary">
          {i18n.t("aiCalendarWizard.step6.closingMessage")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">
        {i18n.t("aiCalendarWizard.step6.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n.t("aiCalendarWizard.step6.description")}
      </Typography>

      {localError && (
        <Alert severity="error" onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      {/* Summary Preview */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {i18n.t("aiCalendarWizard.step6.readyToSchedule")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("aiCalendarWizard.step6.posts")}
              </Typography>
              <Typography variant="h6">{state.generatedPosts.length}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("aiCalendarWizard.step6.accounts")}
              </Typography>
              <Typography variant="h6">{state.selectedAccounts.length}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("aiCalendarWizard.step6.platforms")}
              </Typography>
              <Typography variant="h6">
                {new Set(state.generatedPosts.map((p) => p.platform)).size}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("aiCalendarWizard.step6.dateRange")}
              </Typography>
              <Typography variant="body2">
                {state.timeRange?.start_date}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Options */}
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={saveAsDraft}
              onChange={(e) => setSaveAsDraft(e.target.checked)}
            />
          }
          label={i18n.t("aiCalendarWizard.step6.saveAsDraft")}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          {i18n.t("aiCalendarWizard.step6.draftHint")}
        </Typography>

        {saveAsDraft && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <FormControl fullWidth size="small" disabled={collectionsLoading}>
              <InputLabel>
                {i18n.t("aiCalendarWizard.step6.draftCollection")}
              </InputLabel>
              <Select
                value={selectedCollectionId}
                label={i18n.t("aiCalendarWizard.step6.draftCollection")}
                onChange={(e) => setSelectedCollectionId(e.target.value)}
              >
                {collections.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title}
                  </MenuItem>
                ))}
                <MenuItem value={NEW_COLLECTION_SENTINEL}>
                  + {i18n.t("aiCalendarWizard.step6.createNewCollection")}
                </MenuItem>
              </Select>
            </FormControl>
            {isCreatingNewCollection && (
              <TextField
                size="small"
                label={i18n.t("aiCalendarWizard.step6.newCollectionName")}
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                inputProps={{ maxLength: 255 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={prevStep} disabled={finalizing}>
          {i18n.t("aiCalendarWizard.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleFinalize}
          disabled={finalizing}
          startIcon={finalizing && <CircularProgress size={20} />}
        >
          {finalizing
            ? i18n.t("aiCalendarWizard.step6.finalizing")
            : i18n.t("aiCalendarWizard.step6.confirm")}
        </Button>
      </Box>
    </Box>
  );
}
